import { PrimaryButton, ReactIcons, SimpleButton } from "@kalyzee/kalyzee-ui";
import React, { useCallback, useMemo, useState } from "react";
import { MasterUI } from "../../../../components/common/MasterUI";
import { PageMasterMode, usePageContext } from "../../../../components/navigation/PageContext";
import { AnimatedBackground } from "../../../../components/utils/AnimatedBackground";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { getGlobalQueryLabel, NavigateGlobalQuery } from "../../../../helpers/navigation";
import { toastError, toastSuccess } from "../../../../helpers/toast";
import styles from "./join.module.css";

export const JoinPage = () => {
  const pageContext = usePageContext<SessionMode.MASTER>();
  const shortId = pageContext?.params?.id;
  const [canShare] = useState(() => typeof navigator !== "undefined" && !!navigator.share);

  const shareUrl = useMemo(() => {
    if (!shortId) return undefined;
    const url = new URL(window.location.href);
    // Keep only `provider` (the lobby needs it to resolve a provider-scoped id);
    // drop everything else so we never share a token or other transient query.
    const provider = url.searchParams.get(getGlobalQueryLabel(NavigateGlobalQuery.PROVIDER));
    url.pathname = `/lobby/${shortId}`;
    url.search = provider ? `?${getGlobalQueryLabel(NavigateGlobalQuery.PROVIDER)}=${provider}` : "";
    url.hash = "";
    return url.toString();
  }, [shortId]);

  const onCopy = useCallback(() => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      toastSuccess("Lien copié dans le presse-papier");
    }).catch(err => {
      toastError("Impossible de copier le lien dans le presse-papier");
      console.error("Failed to copy share URL: ", err);
    });
  }, [shareUrl]);

  const onShare = useCallback(() => {
    if (!shareUrl) return;
    navigator.share({ title: "Partager le lien", url: shareUrl }).catch(err => {
      // The user cancelling the share sheet rejects the promise — ignore it.
      if (err?.name !== "AbortError") console.error("Failed to share URL: ", err);
    });
  }, [shareUrl]);

  const onPrimary = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    if (!shortId) return;
    // The lobby is public and handles login/guest access itself; it needs the
    // current `provider` (if any) to resolve the id.
    pageContext?.goTo(PageMasterMode.LOBBY, { id: shortId }, { replace: true, forwardParams: [NavigateGlobalQuery.PROVIDER] });
  }, [pageContext, shortId]);

  return (
    <MasterUI>
      <AnimatedBackground contentClassName={styles.container}>
        {shareUrl ? (
          <div className={styles.card}>
            <span className={styles.title}>Lien de l'événement</span>

            <div className={styles.linkRow}>
              <div className={styles.linkScroll}>
                <a className={styles.linkText} href={shareUrl} title={shareUrl} onClick={onPrimary}>{shareUrl}</a>
              </div>
              <SimpleButton className={styles.copyBtn} onClick={onCopy} label="Copier" />
            </div>

            {canShare && (
              <PrimaryButton className={styles.primaryButton} onClick={onShare}>
                Partager
              </PrimaryButton>
            )}
          </div>
        ) : (
          <div className={styles.notFound}>
            <ReactIcons.QRCode style={{ width: 48, height: 48, opacity: 0.4 }} />
            <span>Lien invalide</span>
          </div>
        )}
      </AnimatedBackground>
    </MasterUI>
  );
};

export default JoinPage;
